import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';
import { 
  ArrowLeft, User, Phone, Calendar, 
  Send, Clock, FileText, Pill, Filter, X, CheckCircle,
  Edit, Save, Trash2, AlertTriangle, 
  RefreshCw, HelpCircle,
  CalendarCheck, PhoneCall, UserCheck, Zap, FileX, Info, Ban, XCircle, MinusCircle,
  Voicemail, PhoneOff, PhoneMissed, ChevronDown, MessageSquare, Activity, Bookmark, Check
} from 'lucide-react';

const TrackerDetails = ({ idProp = null, onClose = null }) => {
  const params = useParams();
  const id = idProp ?? params.id;
  const navigate = useNavigate();
  const isEmbedded = typeof onClose === 'function';
  const handleBack = () => { if (isEmbedded) onClose(); else navigate(-1); };
  
  const location = useLocation();
  const draftStatus = location.state?.draftStatus;
  const draftRemark = location.state?.draftRemark;

  const [info, setInfo] = useState(null); 
  const [patientPrescriptions, setPatientPrescriptions] = useState([]); 
  const [remarks, setRemarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedRxs, setSelectedRxs] = useState([]);

  // Edit Prescription Modal State
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  
  // Main Form State
  const [customStatus, setCustomStatus] = useState('');
  
  // Delete Remark State
  const [showDeleteRemarkModal, setShowDeleteRemarkModal] = useState(false);
  const [remarkToDelete, setRemarkToDelete] = useState(null);

  // ✅ NEW: Edit Log/History State
  const [showEditLogModal, setShowEditLogModal] = useState(false);
  const [editLogData, setEditLogData] = useState({ id: null, title: '', status: '', remark: '' });

  // ✅ Edit Record (prescriber/patient info) State
  const [showRecordEditModal, setShowRecordEditModal] = useState(false);
  const [recordEditForm, setRecordEditForm] = useState({
      id: null,
      prescriber_name: '',
      prescriber_phone: '',
      patient_name: '',
      patient_phone: '',
      dob: '',
      insurance_provider: '',
      latest_call_status: ''
  });

  // ✅ Delete Record State
  const [showRecordDeleteModal, setShowRecordDeleteModal] = useState(false);

  // � New design state
  const [rxTab, setRxTab] = useState('all');           // all | pending | completed
  const [contactResult, setContactResult] = useState(''); // optional UI-only metadata
  const [lastSavedAt, setLastSavedAt] = useState('');

  // �🎨 STATUS CONFIGURATION — modern palette with gradient accents
  const STATUS_OPTIONS = [
    { value: 'APPOINTMENT FIRST',      label: 'Appointment First',      icon: CalendarCheck, accent: 'sky',     activeBg: 'bg-sky-50',     activeText: 'text-sky-700',     activeBorder: 'border-sky-300',     activeRing: 'ring-sky-300',     iconBg: 'bg-sky-100',     iconText: 'text-sky-600' },
    { value: 'CALL BACK',              label: 'Call Back',              icon: PhoneCall,     accent: 'violet',  activeBg: 'bg-violet-50',  activeText: 'text-violet-700',  activeBorder: 'border-violet-300',  activeRing: 'ring-violet-300',  iconBg: 'bg-violet-100',  iconText: 'text-violet-600' },
    { value: 'PATIENT CALL',           label: 'Patient Call',           icon: UserCheck,     accent: 'orange',  activeBg: 'bg-orange-50',  activeText: 'text-orange-700',  activeBorder: 'border-orange-300',  activeRing: 'ring-orange-300',  iconBg: 'bg-orange-100',  iconText: 'text-orange-600' },
    { value: 'PROCESSING',             label: 'Processing',             icon: RefreshCw,     accent: 'emerald', activeBg: 'bg-emerald-50', activeText: 'text-emerald-700', activeBorder: 'border-emerald-300', activeRing: 'ring-emerald-300', iconBg: 'bg-emerald-100', iconText: 'text-emerald-600' },
    { value: 'REQUEST ELECTRONICALLY', label: 'Request Electronically', icon: Zap,           accent: 'pink',    activeBg: 'bg-pink-50',    activeText: 'text-pink-700',    activeBorder: 'border-pink-300',    activeRing: 'ring-pink-300',    iconBg: 'bg-pink-100',    iconText: 'text-pink-600' },
    { value: 'DATA NOT FOUND',         label: 'Data Not Found',         icon: FileX,         accent: 'amber',   activeBg: 'bg-amber-50',   activeText: 'text-amber-700',   activeBorder: 'border-amber-300',   activeRing: 'ring-amber-300',   iconBg: 'bg-amber-100',   iconText: 'text-amber-600' },
    { value: 'ADDITIONAL INFO',        label: 'Additional Info',        icon: Info,          accent: 'blue',    activeBg: 'bg-blue-50',    activeText: 'text-blue-700',    activeBorder: 'border-blue-300',    activeRing: 'ring-blue-300',    iconBg: 'bg-blue-100',    iconText: 'text-blue-600' },
    { value: "DON'T NEED",             label: "Don't Need",             icon: Ban,           accent: 'gray',    activeBg: 'bg-gray-800',   activeText: 'text-white',       activeBorder: 'border-gray-900',    activeRing: 'ring-gray-500',    iconBg: 'bg-gray-200',    iconText: 'text-gray-700' },
    { value: 'INVALID',                label: 'Invalid',                icon: XCircle,       accent: 'red',     activeBg: 'bg-red-500',    activeText: 'text-white',       activeBorder: 'border-red-600',     activeRing: 'ring-red-300',     iconBg: 'bg-red-100',     iconText: 'text-red-600' },
    { value: 'DISCONTINUED',           label: 'Discontinued',           icon: MinusCircle,   accent: 'red',     activeBg: 'bg-red-800',    activeText: 'text-white',       activeBorder: 'border-red-900',     activeRing: 'ring-red-400',     iconBg: 'bg-red-100',     iconText: 'text-red-700' },
    { value: 'OTHERS',                 label: 'Others',                 icon: HelpCircle,    accent: 'slate',   activeBg: 'bg-slate-100',  activeText: 'text-slate-700',   activeBorder: 'border-slate-300',   activeRing: 'ring-slate-300',   iconBg: 'bg-slate-200',   iconText: 'text-slate-600' },
  ];

  // Contact-result options (UI-only; saved as a prefix on the log title)
  const CONTACT_RESULTS = [
    { value: 'SPOKE_TO_PATIENT', label: 'Spoke to Patient', icon: PhoneCall,    activeBg: 'bg-emerald-50', activeText: 'text-emerald-700', activeBorder: 'border-emerald-300', activeRing: 'ring-emerald-300', iconBg: 'bg-emerald-100', iconText: 'text-emerald-600' },
    { value: 'LEFT_VOICEMAIL',   label: 'Left Voicemail',   icon: Voicemail,    activeBg: 'bg-violet-50',  activeText: 'text-violet-700',  activeBorder: 'border-violet-300',  activeRing: 'ring-violet-300',  iconBg: 'bg-violet-100',  iconText: 'text-violet-600' },
    { value: 'NO_ANSWER',        label: 'No Answer',         icon: PhoneOff,     activeBg: 'bg-orange-50',  activeText: 'text-orange-700',  activeBorder: 'border-orange-300',  activeRing: 'ring-orange-300',  iconBg: 'bg-orange-100',  iconText: 'text-orange-600' },
    { value: 'WRONG_NUMBER',     label: 'Wrong Number',      icon: PhoneMissed,  activeBg: 'bg-rose-50',    activeText: 'text-rose-700',    activeBorder: 'border-rose-300',    activeRing: 'ring-rose-300',    iconBg: 'bg-rose-100',    iconText: 'text-rose-600' },
  ];

  const getAgentName = () => {
    try {
      const stored = localStorage.getItem('user');
      if (!stored) return 'Unknown Agent';
      const u = JSON.parse(stored);
      return u.full_name || u.name || u.username || 'Agent';
    } catch (err) { return 'Agent'; }
  };

  const agentName = getAgentName(); 

  const getCurrentDateTime = () => {
      const now = new Date();
      now.setMinutes(now.getMinutes() - now.getTimezoneOffset()); 
      return now.toISOString().slice(0, 16);
  };

  const getInitialStatus = () => {
      if (draftStatus) {
          const clean = draftStatus.trim().toUpperCase();
          const match = STATUS_OPTIONS.find(s => s.value === clean);
          if (match) return match.value;
      }
      return 'APPOINTMENT FIRST';
  };

  const [formData, setFormData] = useState({ 
      title: '', 
      remark: draftRemark || '', 
      status: getInitialStatus(), 
      log_date: getCurrentDateTime() 
  });

  // API_URL is now managed in src/config.js


  const fetchPageData = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}?action=get_tracker_detail&id=${id}&_t=${Date.now()}`);
      const data = res.data;
      setInfo(data);
      
      if (data) {
          if (data.rx_no) setSelectedRxs([data.rx_no]);
          if (data.prescriber_name) fetchRemarks(data.prescriber_name);
          if (data.patient_id) fetchAllPatientRx(data.patient_id);
      }
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPageData();
  }, [id, fetchPageData]);

  const fetchAllPatientRx = async (patientId) => {
      try {
          const res = await axios.get(`${API_URL}?action=get_patient_details&id=${patientId}&_t=${Date.now()}`);
          if(res.data && Array.isArray(res.data.prescriptions)) {
              setPatientPrescriptions(res.data.prescriptions);
          }
      } catch (err) { console.error(err); }
  };

  const fetchRemarks = async (doctorName) => {
    try {
      const encodedName = encodeURIComponent(doctorName);
      const res = await axios.get(`${API_URL}?action=get_doctor_remarks&name=${encodedName}&_t=${Date.now()}`);
      if(Array.isArray(res.data)) setRemarks(res.data);
    } catch (err) { console.error(err); }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleStatusSelect = (statusValue) => {
    const statusObj = STATUS_OPTIONS.find(s => s.value === statusValue);
    setFormData(prev => {
        // Auto-fill title if empty or if it matches a previous default label
        const currentTitleMatchesDefault = prev.title === '' || STATUS_OPTIONS.some(opt => opt.label === prev.title);
        return { 
            ...prev, 
            status: statusValue,
            title: currentTitleMatchesDefault ? statusObj.label : prev.title
        };
    });
  };

  // --- EDIT PRESCRIPTION MODAL HANDLERS ---
  const handleEditRx = (rx, e) => {
    e.stopPropagation(); 
    setEditData({ ...rx, ndc: rx.ndc || '' }); 
    setShowEditModal(true);
  };

  const handleEditChange = (e) => {
    setEditData({ ...editData, [e.target.name]: e.target.value });
  };

  const saveEditedRx = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      const response = await axios.post(`${API_URL}?action=update_prescription_modal`, {
        id: editData.id, 
        rx_no: editData.rx_no,
        drug_name: editData.drug_name,
        ndc: editData.ndc,
        quantity: editData.quantity,
        days_supply: editData.days_supply,
        expiration_date: editData.expiration_date,
        status: editData.status
      });

      if (response.data.success) {
          setPatientPrescriptions([]); 
          await fetchAllPatientRx(info.patient_id);
          setShowEditModal(false);
      } else {
          alert("Update failed: " + (response.data.error || "Unknown Error"));
      }
      setSavingEdit(false);
    } catch (err) {
      console.error(err);
      alert('Network Error - Check Console');
      setSavingEdit(false);
    }
  };

  // --- DELETE REMARK HANDLERS ---
  const confirmDeleteRemark = (remark) => {
      setRemarkToDelete(remark);
      setShowDeleteRemarkModal(true);
  };

  const executeDeleteRemark = async () => {
      if (!remarkToDelete) return;
      try {
          await axios.post(`${API_URL}?action=delete_remark`, { id: remarkToDelete.remarksid });
          setShowDeleteRemarkModal(false);
          setRemarkToDelete(null);
          fetchRemarks(info.prescriber_name);
      } catch (err) {
          console.error(err);
          alert("Failed to delete remark.");
      }
  };

  // --- ADD REMARK HANDLER ---
  const handleAddRemark = async (e, opts = {}) => {
    if (e && e.preventDefault) e.preventDefault();
    const { isDraft = false } = opts;

    let finalStatus = formData.status;
    if (formData.status === 'OTHERS') {
        if (!customStatus.trim()) {
            alert("Please specify the details for 'Others'.");
            return;
        }
        finalStatus = `OTHERS: ${customStatus}`;
    }

    // Prefix Contact Result onto the title so it's preserved in the log
    const contactLabel = CONTACT_RESULTS.find(c => c.value === contactResult)?.label;
    const baseTitle = (formData.title || STATUS_OPTIONS.find(s => s.value === formData.status)?.label || 'Call Log');
    const finalTitle = contactLabel ? `${contactLabel} — ${baseTitle}` : baseTitle;

    setSaving(true);

    try {
      const remarkRes = await axios.post(`${API_URL}?action=add_remark`, {
        prescriber_name: info.prescriber_name,
        rx_no: selectedRxs.length > 0 ? selectedRxs : [''], 
        title: finalTitle, 
        remarks: formData.remark || (isDraft ? '[Draft]' : ''), 
        status: finalStatus, 
        added_by: agentName,
        log_date: formData.log_date 
      });

      if (!remarkRes.data.success) {
          throw new Error("Failed to save remark");
      }

      if (selectedRxs.length > 0) {
          for (const rx of selectedRxs) {
              await axios.post(`${API_URL}?action=update_prescription_status`, {
                  rx_no: rx, 
                  status: finalStatus
              });
          }
          if (info.patient_id) {
              await fetchAllPatientRx(info.patient_id);
          }
      }
      
      setFormData(prev => ({ 
          ...prev, 
          remark: '', 
          title: '', 
          status: 'APPOINTMENT FIRST',
          log_date: getCurrentDateTime()
      }));
      setCustomStatus(''); 
      setContactResult('');
      // Uncheck Rx selections after a completed call so the user starts fresh for the next log
      setSelectedRxs([]);
      setLastSavedAt(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
      fetchRemarks(info.prescriber_name);
      
    } catch (err) { 
        console.error("Error Saving:", err);
        alert("Error saving record."); 
    } finally {
        setSaving(false); 
    }
  };

  // ✅ NEW: EDIT HISTORY LOG HANDLERS
  const handleEditLogClick = (e, log) => {
      e.stopPropagation();
      setEditLogData({
          id: log.remarksid,
          title: log.title || '',
          status: log.status || 'APPOINTMENT FIRST', // Default to avoid controlled/uncontrolled warning
          remark: log.remarks || ''
      });
      setShowEditLogModal(true);
  };

  const handleSaveEditedLog = async (e) => {
      e.preventDefault();
      try {
          await axios.post(`${API_URL}?action=update_remark`, editLogData);
          setShowEditLogModal(false);
          fetchRemarks(info.prescriber_name); // Refresh list
      } catch (error) {
          console.error(error);
          alert("Error updating log.");
      }
  };

  const filteredRemarks = selectedRxs.length > 0
    ? remarks.filter(r => selectedRxs.includes(r.rx_no)) 
    : remarks;

  // Rx Selection Handlers
  const toggleRx = (rx_no) => {
      setSelectedRxs(prev => prev.includes(rx_no) ? prev.filter(r => r !== rx_no) : [...prev, rx_no]);
  };
  const selectAllRx = () => {
      setSelectedRxs(patientPrescriptions.map(rx => rx.rx_no));
  };

  const formatCardDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-PH', { month: 'short', day: '2-digit', year: 'numeric' }) + ' ' + date.toLocaleTimeString('en-PH', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === '0000-00-00') return '-';
    return new Date(dateString).toLocaleDateString('en-US');
  };

  const getStatusConfig = (statusStr) => {
    if(!statusStr) return STATUS_OPTIONS[0];
    const cleanStatus = statusStr.split(':')[0].trim().toUpperCase();
    return STATUS_OPTIONS.find(s => s.value === cleanStatus) || 
           { icon: FileText, label: statusStr, iconBg: 'bg-gray-100', iconText: 'text-gray-600', activeBg: 'bg-gray-100', activeText: 'text-gray-600', activeBorder: 'border-gray-300' };
  };

  // ✅ Record Edit/Delete handlers
  const openRecordEdit = () => {
      if (!info) return;
      const cleanDob = (info.dob && info.dob !== '0000-00-00') ? String(info.dob).split(' ')[0] : '';
      setRecordEditForm({
          id: info.id,
          prescriber_name: info.prescriber_name || '',
          prescriber_phone: info.prescriber_phone || '',
          patient_name: info.patient_name || '',
          patient_phone: info.patient_phone || '',
          dob: cleanDob,
          insurance_provider: info.insurance_provider || '',
          latest_call_status: info.latest_call_status || 'Pending'
      });
      setShowRecordEditModal(true);
  };

  const handleRecordUpdateSubmit = async (e) => {
      e.preventDefault();
      try {
          await axios.post(`${API_URL}?action=update_prescriber_info`, recordEditForm);
          setShowRecordEditModal(false);
          fetchPageData();
      } catch (err) {
          console.error(err);
          alert('Error updating record.');
      }
  };

  const executeRecordDelete = async () => {
      if (!info) return;
      try {
          await axios.post(`${API_URL}?action=delete_prescription`, { id: info.id });
          setShowRecordDeleteModal(false);
          if (isEmbedded && onClose) onClose();
          else navigate(-1);
      } catch (err) {
          console.error(err);
          alert('Error deleting record.');
      }
  };

  // Helpers / computed values for the new design
  const getInitials = (name) => {
    if (!name) return '??';
    const parts = String(name).replace(',', '').split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || (parts[0]?.[1] || ''))).toUpperCase();
  };

  const isPendingRx = (rx) => {
    const s = String(rx?.status || '').trim().toUpperCase();
    return !s || s === 'PENDING';
  };

  const rxCounts = {
    all: patientPrescriptions.length,
    pending: patientPrescriptions.filter(isPendingRx).length,
    completed: patientPrescriptions.filter(r => !isPendingRx(r)).length
  };

  const filteredRx = patientPrescriptions.filter(r => {
    if (rxTab === 'pending') return isPendingRx(r);
    if (rxTab === 'completed') return !isPendingRx(r);
    return true;
  });

  const selectedRx = selectedRxs.length > 0
    ? patientPrescriptions.find(r => r.rx_no === selectedRxs[0])
    : null;

  if (loading) return <div className="flex h-screen justify-center items-center text-gray-400">Loading...</div>;
  if (!info || info.error) return <div className="flex h-screen justify-center items-center text-red-400">Record not found.</div>;

  return (
    <div className={`${isEmbedded ? 'p-6 pb-24' : 'min-h-screen p-6 pb-24'} bg-gray-100 flex flex-col font-sans relative`}>

      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-r from-green-800 to-teal-600 rounded-2xl shadow-lg mb-5 px-5 py-4">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 30%, white 1px, transparent 1px)', backgroundSize: '40px 40px'}}></div>
        <div className="relative flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <button onClick={handleBack} className="flex items-center justify-center w-10 h-10 rounded-xl bg-white/15 backdrop-blur-sm border border-white/30 text-white hover:bg-white/25 transition shadow" title={isEmbedded ? 'Close' : 'Back'}>
              {isEmbedded ? <X size={18}/> : <ArrowLeft size={18}/>}
            </button>
            <div>
              <h1 className="text-lg font-bold text-white tracking-tight flex items-center gap-2"><Activity size={18}/> Tracker Details</h1>
              <p className="text-[11px] text-green-100/80 mt-0.5">Call Logs &amp; Remarks Management</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button onClick={openRecordEdit} className="flex items-center gap-1.5 h-9 px-3 rounded-lg bg-white text-teal-700 hover:bg-teal-50 border border-white/40 text-xs font-bold transition shadow"><Edit size={13}/> Edit</button>
            <button onClick={() => setShowRecordDeleteModal(true)} className="flex items-center gap-1.5 h-9 px-3 rounded-lg bg-white text-rose-600 hover:bg-rose-50 border border-white/40 text-xs font-bold transition shadow"><Trash2 size={13}/> Delete</button>
            {info?.prescriber_name && (
              <div className="hidden md:flex items-center gap-2 bg-white/15 hover:bg-white/25 backdrop-blur-sm rounded-xl pl-2 pr-2 py-1.5 border border-white/30 transition cursor-default">
                <div className="flex items-center justify-center w-7 h-7 rounded-full bg-white/25 text-white"><User size={13}/></div>
                <div className="text-left leading-tight">
                  <p className="text-[9px] uppercase tracking-wider text-green-100/80 font-semibold">Prescriber</p>
                  <p className="text-xs font-bold text-white truncate max-w-[160px]">{info.prescriber_name}</p>
                </div>
                <ChevronDown size={14} className="text-white/80"/>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-stretch">

        {/* --- CARD 1: PATIENT + PRESCRIPTIONS --- */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex flex-col h-full min-h-[600px] hover:shadow-md transition-shadow">

          {/* Patient header */}
          <div className="flex items-start gap-3 pb-4 border-b border-gray-100">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center text-white text-sm font-bold shadow flex-shrink-0">{getInitials(info.patient_name)}</div>
            <div className="flex-1 min-w-0">
              <h2 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1.5"><User size={11}/> Patient</h2>
              <p className="text-base font-bold text-gray-800 leading-tight truncate">{info.patient_name}</p>
              <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5">
                <span className="text-[11px] text-gray-500 flex items-center gap-1"><Phone size={11}/> {info.patient_phone || '-'}</span>
                <span className="text-[11px] text-gray-500 flex items-center gap-1"><Calendar size={11}/> {formatDate(info.dob)}</span>
              </div>
            </div>
          </div>

          {/* Prescriber */}
          <div className="py-3 border-b border-gray-100">
            <h2 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1.5 mb-0.5"><User size={11}/> Prescriber</h2>
            <p className="text-sm font-bold text-teal-700">{info.prescriber_name}</p>
            <span className="text-[11px] text-gray-500 flex items-center gap-1 mt-0.5"><Phone size={11}/> {info.prescriber_phone || '-'}</span>
          </div>

          {/* Active prescriptions */}
          <div className="pt-3 flex-1 flex flex-col min-h-0">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-[10px] font-bold text-teal-600 uppercase tracking-wider flex items-center gap-1.5"><Pill size={12}/> Active Prescriptions</h2>
              <span className="bg-teal-100 text-teal-700 text-[10px] font-bold px-2 py-0.5 rounded-full">{rxCounts.all} Active</span>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 mb-3 bg-gray-100 p-1 rounded-lg">
              {[
                { k: 'all', label: 'All' },
                { k: 'pending', label: 'Pending' },
                { k: 'completed', label: 'Completed' }
              ].map(t => (
                <button
                  key={t.k}
                  type="button"
                  onClick={() => setRxTab(t.k)}
                  className={`flex-1 text-[11px] font-bold py-1.5 rounded transition ${rxTab === t.k ? 'bg-white text-teal-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  {t.label} ({rxCounts[t.k]})
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto pr-1 custom-scrollbar space-y-2">
              {filteredRx.length === 0 ? (
                <p className="text-center text-xs text-gray-400 italic py-8">No prescriptions in this tab.</p>
              ) : filteredRx.map((rx, index) => {
                const isSelected = selectedRxs.includes(rx.rx_no);
                const pending = isPendingRx(rx);
                const leftBorder = isSelected ? 'border-l-teal-500' : (pending ? 'border-l-orange-300' : 'border-l-emerald-400');
                const bg = isSelected ? 'bg-teal-50' : 'bg-white';
                return (
                  <div
                    key={index}
                    onClick={() => toggleRx(rx.rx_no)}
                    className={`group relative cursor-pointer rounded-lg border border-gray-200 ${bg} ${leftBorder} border-l-4 p-2.5 transition hover:shadow-sm hover:border-teal-200`}
                  >
                    <div className="flex items-start gap-2.5">
                      {isSelected ? (
                        <div className="w-7 h-7 rounded-full bg-teal-500 text-white flex items-center justify-center flex-shrink-0 shadow"><Check size={14}/></div>
                      ) : (
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${pending ? 'bg-orange-100 text-orange-500' : 'bg-emerald-100 text-emerald-600'}`}><Pill size={14}/></div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm font-bold text-gray-800 truncate">{rx.drug_name}</p>
                            <p className="text-[10px] text-gray-500 font-mono">RX #{rx.rx_no}</p>
                          </div>
                          <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full uppercase ${pending ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>{rx.status || 'Pending'}</span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 mt-1.5 text-[10px]">
                          <span className="flex items-center gap-1"><span className="text-gray-400">Qty</span><b className="text-gray-700">{rx.quantity}</b></span>
                          <span className="flex items-center gap-1"><span className="text-gray-400">Days</span><b className="text-gray-700">{rx.days_supply}</b></span>
                          <span className="flex items-center gap-1"><span className="text-gray-400">Exp</span><b className="text-rose-500">{formatDate(rx.expiration_date)}</b></span>
                        </div>
                      </div>
                      <button onClick={(e) => handleEditRx(rx, e)} className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-teal-600 transition" title="Edit RX"><Edit size={12}/></button>
                    </div>
                  </div>
                );
              })}
            </div>

            {(patientPrescriptions.length > 1 || selectedRxs.length > 0) && (
              <div className="flex items-center justify-end gap-3 pt-2 mt-1 border-t border-gray-100">
                {patientPrescriptions.length > 1 && (
                  <button onClick={selectAllRx} className="text-[10px] text-teal-600 hover:text-teal-800 font-bold">Select All</button>
                )}
                {selectedRxs.length > 0 && (
                  <button onClick={() => setSelectedRxs([])} className="text-[10px] text-gray-400 hover:text-red-500 flex items-center gap-1"><X size={10}/> Clear</button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* --- CARD 2: SELECTED RX + CALL FORM --- */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex flex-col h-full min-h-[600px] hover:shadow-md transition-shadow">

          {/* Selected RX banner */}
          <div className="border border-gray-100 bg-gradient-to-br from-teal-50/40 to-white rounded-xl p-3 mb-4">
            {selectedRx ? (
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-teal-100 text-teal-600 flex items-center justify-center flex-shrink-0"><Pill size={18}/></div>
                <div className="flex-1 min-w-0">
                  <span className="inline-block bg-teal-100 text-teal-700 text-[9px] font-bold uppercase px-2 py-0.5 rounded">Selected RX</span>
                  <p className="text-base font-bold text-gray-800 mt-0.5 truncate">{selectedRx.drug_name}</p>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-gray-500 mt-0.5">
                    <span className="font-mono">RX #{selectedRx.rx_no}</span>
                    <span>Qty <b className="text-gray-700">{selectedRx.quantity}</b></span>
                    <span><b className="text-gray-700">{selectedRx.days_supply}</b> Days</span>
                    <span>Expires <b className="text-rose-500">{formatDate(selectedRx.expiration_date)}</b></span>
                  </div>
                </div>
                {selectedRxs.length > 1 && (
                  <span className="bg-teal-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">+{selectedRxs.length - 1}</span>
                )}
              </div>
            ) : (
              <p className="text-xs text-gray-400 italic text-center py-2">Select an RX from the left to begin a call.</p>
            )}
          </div>

          {/* Call Information header */}
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-[11px] font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5"><Phone size={12}/> Call Information</h3>
            <div className="flex items-center gap-1 text-teal-700">
              <input
                type="datetime-local"
                name="log_date"
                value={formData.log_date}
                onChange={handleInputChange}
                className="text-[11px] font-semibold border-none focus:ring-0 cursor-pointer bg-transparent"
              />
              <Calendar size={12} className="text-gray-400"/>
            </div>
          </div>

          <form onSubmit={handleAddRemark} className="flex flex-col gap-4 flex-1">
            <div>
              <label className="block text-[10px] font-bold text-gray-500 mb-1">Title / Reason</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter call title or reason..."
                className="w-full border border-gray-200 rounded-lg p-2 text-sm focus:ring-1 focus:ring-teal-500 focus:border-teal-500 outline-none"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2.5">
                <h3 className="text-[11px] font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                  <Activity size={12} className="text-teal-600"/> Call Outcome
                </h3>
                {formData.status && (
                  <button
                    type="button"
                    onClick={() => { handleStatusSelect(''); setCustomStatus(''); }}
                    className="text-[10px] font-semibold text-gray-400 hover:text-gray-600 flex items-center gap-1 transition"
                    title="Clear selection"
                  >
                    <X size={11}/> Clear
                  </button>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                {STATUS_OPTIONS.map(opt => {
                  const Icon = opt.icon;
                  const active = formData.status === opt.value;
                  return (
                    <button
                      type="button"
                      key={opt.value}
                      onClick={() => handleStatusSelect(opt.value)}
                      title={opt.label}
                      className={`group inline-flex items-center gap-2 px-3.5 py-2 rounded-full border transition-all duration-150 text-xs font-semibold whitespace-nowrap ${active
                        ? `${opt.activeBg} ${opt.activeText} ${opt.activeBorder} shadow-sm ring-2 ring-offset-1 ${opt.activeRing}`
                        : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50 hover:shadow-sm'}`}
                    >
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${active ? 'bg-white/50' : `${opt.iconBg} ${opt.iconText}`}`}>
                        <Icon size={12}/>
                      </span>
                      <span className="leading-none">{opt.label}</span>
                    </button>
                  );
                })}
              </div>

              <div className={`transition-all duration-300 ease-in-out overflow-hidden ${formData.status === 'OTHERS' ? 'max-h-20 opacity-100 mt-3' : 'max-h-0 opacity-0'}`}>
                <input
                  type="text"
                  value={customStatus}
                  onChange={(e) => setCustomStatus(e.target.value)}
                  className="w-full border border-teal-300 bg-teal-50 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                  placeholder="Please specify outcome details..."
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-bold text-gray-500 mb-1">Agent Remarks <span className="font-normal text-gray-400 normal-case">(Optional)</span></label>
              <div className="relative">
                <textarea
                  name="remark"
                  value={formData.remark}
                  onChange={handleInputChange}
                  maxLength={1000}
                  className="w-full h-20 border border-gray-200 rounded-lg p-3 pr-16 text-sm focus:ring-1 focus:ring-teal-500 focus:border-teal-500 outline-none resize-none"
                  placeholder="Type call details, patient response, next steps..."
                />
                <span className="absolute bottom-2 right-3 text-[10px] text-gray-400">{(formData.remark || '').length}/1000</span>
              </div>
            </div>

            {/* hidden submit so Enter key still triggers complete-call */}
            <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1}/>
          </form>
        </div>

        {/* --- CARD 3: ACTIVITY TIMELINE --- */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex flex-col h-full min-h-[600px] hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[11px] font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5"><Clock size={12}/> Activity Timeline</h2>
            <button type="button" className="flex items-center gap-1 text-[11px] font-semibold text-gray-500 border border-gray-200 px-2.5 py-1 rounded-lg hover:bg-gray-50 transition">
              <Filter size={11}/> Filter
              {selectedRxs.length > 0 && <span className="bg-teal-100 text-teal-700 text-[9px] px-1.5 rounded-full font-bold">{selectedRxs.length}</span>}
            </button>
          </div>

          <div className="flex-1 overflow-y-auto pr-1 custom-scrollbar">
            {filteredRemarks.length === 0 ? (
              <div className="h-full flex items-center justify-center text-gray-400 text-xs italic border border-dashed border-gray-200 rounded-lg p-6 text-center">
                {selectedRxs.length > 0 ? 'No logs for selected RX(s).' : 'No history yet — log your first call.'}
              </div>
            ) : (
              <div className="relative pl-1">
                <div className="absolute left-[18px] top-2 bottom-2 w-px bg-gray-200"></div>
                <div className="space-y-4">
                  {filteredRemarks.map((rem) => {
                    const cfg = getStatusConfig(rem.status);
                    const Icon = cfg.icon;
                    const isCurrent = rem === filteredRemarks[0];
                    return (
                      <div key={rem.remarksid} className="relative flex gap-3 group">
                        <div className={`relative z-10 w-9 h-9 rounded-full ${cfg.iconBg || 'bg-gray-100'} flex items-center justify-center border-4 border-white shadow-sm flex-shrink-0`}>
                          <Icon size={14} className={cfg.iconText || 'text-gray-600'}/>
                        </div>
                        <div className="flex-1 min-w-0 pb-1">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-[10px] text-gray-500">{formatCardDate(rem.timestamp)}</p>
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                              <button onClick={(e) => handleEditLogClick(e, rem)} className="p-1 text-gray-400 hover:text-blue-600" title="Edit Log"><Edit size={11}/></button>
                              <button onClick={(e) => { e.stopPropagation(); confirmDeleteRemark(rem); }} className="p-1 text-gray-400 hover:text-red-600" title="Delete Log"><Trash2 size={11}/></button>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <h4 className="text-sm font-bold text-gray-800 leading-tight">{rem.title || cfg.label}</h4>
                            {isCurrent && <span className="bg-teal-100 text-teal-700 text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase">Current</span>}
                          </div>
                          <p className="text-[10px] text-gray-500 mt-0.5"><span className="font-semibold">Agent:</span> {rem.added_by || 'System'}</p>
                          {rem.remarks && rem.remarks !== '[Draft]' && (
                            <p className="text-xs text-gray-600 mt-1 whitespace-pre-wrap leading-relaxed">{rem.remarks}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <button type="button" className="mt-3 w-full text-[11px] font-bold text-teal-700 hover:bg-teal-50 py-2 rounded-lg border border-teal-100 flex items-center justify-center gap-1 transition">
            View all history <ChevronDown size={12}/>
          </button>
        </div>

      </div>

      {/* Sticky footer action bar */}
      <div className={`${isEmbedded ? 'sticky' : 'fixed'} bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-gray-200 px-6 py-3 flex items-center justify-between gap-3 shadow-[0_-4px_16px_rgba(0,0,0,0.05)] z-40`}>
        <div className="flex items-center gap-2 text-[11px] text-gray-500">
          <span className={`w-2 h-2 rounded-full ${lastSavedAt ? 'bg-emerald-400' : 'bg-amber-400 animate-pulse'}`}></span>
          {lastSavedAt
            ? <span><b className="text-gray-700">Saved</b><span className="text-gray-400"> · Last saved: <b className="text-gray-700">{lastSavedAt}</b></span></span>
            : <span><b className="text-gray-700">Unsaved changes</b></span>}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={handleBack} className="px-4 py-2 text-xs font-bold text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition">Cancel</button>
          <button type="button" onClick={(e) => handleAddRemark(e, { isDraft: true })} disabled={saving} className="px-4 py-2 text-xs font-bold text-teal-700 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100 transition flex items-center gap-1.5 disabled:opacity-60"><Bookmark size={13}/> Save Draft</button>
          <button type="button" onClick={(e) => handleAddRemark(e)} disabled={saving} className="px-5 py-2 text-xs font-bold text-white bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 rounded-lg flex items-center gap-1.5 shadow disabled:opacity-60 transition">
            <CheckCircle size={13}/> {saving ? 'Saving...' : 'Complete Call'}
          </button>
        </div>
      </div>

      {/* DELETE CONFIRMATION MODAL */}
      {showDeleteRemarkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-80 p-6 relative text-center animate-fade-in-up">
                <div className="mx-auto flex items-center justify-center h-10 w-10 rounded-full bg-red-100 mb-4">
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Delete Log?</h3>
                <p className="text-xs text-gray-500 mt-2 mb-6">
                    This action cannot be undone. Are you sure you want to delete this call log?
                </p>
                <div className="flex justify-center gap-3">
                    <button onClick={() => setShowDeleteRemarkModal(false)} className="flex-1 bg-white text-gray-700 border border-gray-300 py-2 rounded font-medium text-sm hover:bg-gray-50">Cancel</button>
                    <button onClick={executeDeleteRemark} className="flex-1 bg-red-600 text-white py-2 rounded font-medium text-sm hover:bg-red-700">Delete</button>
                </div>
            </div>
        </div>
      )}

      {/* ✅ NEW: EDIT LOG MODAL */}
      {showEditLogModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md animate-fade-in-up">
                <div className="bg-blue-600 px-5 py-3 flex justify-between items-center rounded-t-lg">
                    <h3 className="text-white font-bold text-sm flex items-center gap-2"><Edit size={16}/> Edit Call Log</h3>
                    <button onClick={() => setShowEditLogModal(false)} className="text-blue-100 hover:text-white"><X size={18}/></button>
                </div>
                <form onSubmit={handleSaveEditedLog} className="p-6 flex flex-col gap-4">
                    <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1">Title</label>
                        <input 
                            type="text" 
                            className="w-full border rounded p-2 text-sm focus:ring-1 focus:ring-blue-500 outline-none" 
                            value={editLogData.title} 
                            onChange={e => setEditLogData({...editLogData, title: e.target.value})}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1">Outcome</label>
                        <select 
                            className="w-full border rounded p-2 text-sm bg-white"
                            value={editLogData.status}
                            onChange={e => setEditLogData({...editLogData, status: e.target.value})}
                        >
                            {STATUS_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1">Remarks</label>
                        <textarea 
                            className="w-full h-32 border rounded p-2 text-sm focus:ring-1 focus:ring-blue-500 outline-none resize-none"
                            value={editLogData.remark}
                            onChange={e => setEditLogData({...editLogData, remark: e.target.value})}
                        ></textarea>
                    </div>
                    <div className="flex gap-3 mt-2">
                        <button type="button" onClick={() => setShowEditLogModal(false)} className="flex-1 bg-gray-100 text-gray-600 py-2 rounded text-sm font-bold hover:bg-gray-200">Cancel</button>
                        <button type="submit" className="flex-1 bg-blue-600 text-white py-2 rounded text-sm font-bold hover:bg-blue-700">Save Changes</button>
                    </div>
                </form>
            </div>
        </div>
      )}

      {/* Edit Rx Modal */}
      {showEditModal && editData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden animate-fade-in-up">
                <div className="bg-teal-600 px-5 py-4 flex justify-between items-center">
                    <h3 className="text-white font-bold text-sm flex items-center gap-2"><Edit size={16}/> Edit Prescription: {editData.rx_no}</h3>
                    <button onClick={() => setShowEditModal(false)} className="text-teal-100 hover:text-white"><X size={18}/></button>
                </div>
                
                <form onSubmit={saveEditedRx} className="p-6 flex flex-col gap-4">
                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase">Drug Name</label>
                        <input type="text" name="drug_name" value={editData.drug_name} onChange={handleEditChange} className="w-full border border-gray-300 rounded p-2 text-sm mt-1 focus:ring-1 focus:ring-teal-500 outline-none" />
                    </div>

                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase">NDC</label>
                        <input 
                            type="text" 
                            name="ndc" 
                            value={editData.ndc} 
                            onChange={handleEditChange} 
                            className="w-full border border-gray-300 rounded p-2 text-sm mt-1 focus:ring-1 focus:ring-teal-500 outline-none" 
                            placeholder="00000-0000-00"
                        />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase">Quantity</label>
                            <input type="number" name="quantity" value={editData.quantity} onChange={handleEditChange} className="w-full border border-gray-300 rounded p-2 text-sm mt-1 outline-none"/>
                        </div>
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase">Days Supply</label>
                            <input type="number" name="days_supply" value={editData.days_supply} onChange={handleEditChange} className="w-full border border-gray-300 rounded p-2 text-sm mt-1 outline-none"/>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase">Expiration</label>
                            <input type="date" name="expiration_date" value={editData.expiration_date} onChange={handleEditChange} className="w-full border border-gray-300 rounded p-2 text-sm mt-1 outline-none"/>
                        </div>
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase">Status</label>
                            <select name="status" value={editData.status} onChange={handleEditChange} className="w-full border border-gray-300 rounded p-2 text-sm mt-1 outline-none bg-white">
                                {STATUS_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="mt-4 flex gap-3">
                        <button type="button" onClick={() => setShowEditModal(false)} className="flex-1 bg-gray-100 text-gray-600 py-2 rounded font-bold text-sm hover:bg-gray-200 transition">Cancel</button>
                        <button type="submit" disabled={savingEdit} className="flex-1 bg-teal-600 text-white py-2 rounded font-bold text-sm hover:bg-teal-700 transition flex justify-center items-center gap-2">
                           {savingEdit ? 'Saving...' : <><Save size={14}/> Save Changes</>}
                        </button>
                    </div>
                </form>
            </div>
        </div>
      )}

      {/* ✅ EDIT RECORD MODAL (prescriber/patient info) */}
      {showRecordEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-[500px] p-6 relative">
            <button onClick={() => setShowRecordEditModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><X size={24} /></button>
            <h3 className="text-xl font-bold mb-4 text-teal-700">Edit Record Details</h3>

            <form onSubmit={handleRecordUpdateSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Prescriber Name</label>
                  <input type="text" className="w-full border p-2 rounded text-sm" value={recordEditForm.prescriber_name} onChange={e => setRecordEditForm({ ...recordEditForm, prescriber_name: e.target.value })} />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Prescriber Phone</label>
                  <input type="text" className="w-full border p-2 rounded text-sm" value={recordEditForm.prescriber_phone} onChange={e => setRecordEditForm({ ...recordEditForm, prescriber_phone: e.target.value })} />
                </div>
              </div>

              <hr className="border-gray-100 my-2" />

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Patient Name</label>
                  <input type="text" className="w-full border p-2 rounded text-sm" value={recordEditForm.patient_name} onChange={e => setRecordEditForm({ ...recordEditForm, patient_name: e.target.value })} />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Patient Phone</label>
                  <input type="text" className="w-full border p-2 rounded text-sm" value={recordEditForm.patient_phone} onChange={e => setRecordEditForm({ ...recordEditForm, patient_phone: e.target.value })} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">DOB</label>
                  <input type="date" className="w-full border p-2 rounded text-sm" value={recordEditForm.dob} onChange={e => setRecordEditForm({ ...recordEditForm, dob: e.target.value })} />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Insurance Code</label>
                  <input type="text" className="w-full border p-2 rounded text-sm" value={recordEditForm.insurance_provider} onChange={e => setRecordEditForm({ ...recordEditForm, insurance_provider: e.target.value })} />
                </div>
              </div>

              <button type="submit" className="w-full bg-teal-600 text-white font-bold py-2 rounded hover:bg-teal-700 flex items-center justify-center gap-2 mt-2">
                <Save size={16} /> Update Details
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ✅ DELETE RECORD CONFIRMATION */}
      {showRecordDeleteModal && info && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-96 p-6 relative text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">Confirm Deletion</h3>
            <p className="text-sm text-gray-500 mt-2">
              Are you sure you want to remove this tracker entry for <strong>{info.prescriber_name}</strong>?
            </p>
            <div className="mt-6 flex justify-center gap-3">
              <button onClick={() => setShowRecordDeleteModal(false)} className="bg-white text-gray-700 border border-gray-300 px-4 py-2 rounded font-medium hover:bg-gray-50">Cancel</button>
              <button onClick={executeRecordDelete} className="bg-red-600 text-white px-4 py-2 rounded font-medium hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default TrackerDetails;